import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  Truck,
  Package,
  Heart,
  AlertTriangle,
  RefreshCw,
  Loader2,
  Download,
  Send,
  X,
  FlaskConical,
  Printer,
  FileDown,
  Pencil,
  Check,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import PageHeader from "@/components/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  api,
  FulfillmentImportResult,
  FulfillmentOrder,
  FulfillmentOrderDetail,
  FulfillmentOrderPatch,
  FulfillmentSummary,
  FurgonetkaParcelSettings,
  PaczkomatResolveCandidate,
  type PaczkomatMatchStatus,
} from "@/lib/api";
import { cn } from "@/lib/utils";
import { OpCallout } from "@/components/op/OpCallout";
import { OP_SURFACE } from "@/lib/opSurfaces";

const WORKFLOW_STEPS = [
  "new",
  "shipment_created",
  "dedication",
  "packing",
  "ready_to_ship",
  "shipped",
  "delivered",
] as const;

const STATUS_LABELS: Record<string, string> = {
  new: "Nowe",
  shipment_created: "Wysyłka w Furgonetce",
  dedication: "Dedykacja",
  packing: "Pakowanie",
  ready_to_ship: "Do wysyłki",
  shipped: "Nadane",
  delivered: "Doręczone",
  preparing: "Dedykacja",
  complaint_open: "Reklamacja",
  return_open: "Zwrot",
  ready_to_reship: "Do ponownej wysyłki",
  return_closed: "Zwrot OK",
  withdrawal: "Odstąpienie od umowy",
  cancelled: "Anulowane",
};

const FILTER_STATUSES = [
  ...WORKFLOW_STEPS,
  "cancelled",
  "complaint_open",
  "return_open",
  "ready_to_reship",
  "withdrawal",
] as const;

const ATTENTION_WORKFLOW = new Set([
  "return_open",
  "ready_to_reship",
  "return_closed",
  "complaint_open",
  "withdrawal",
]);

const EXCEPTION_STATUSES = [
  "complaint_open",
  "return_open",
  "ready_to_reship",
  "return_closed",
  "withdrawal",
  "cancelled",
] as const;

/** Statusy, z których wolno zlecić paczkę w Furgonetce. */
function canCreateFurgonetkaPackage(status: string | undefined): boolean {
  return status === "new" || status === "ready_to_reship";
}

function isAttentionOrder(o: FulfillmentOrder): boolean {
  if (
    ATTENTION_WORKFLOW.has(o.fulfillment_status) ||
    Boolean(o.furgonetka_returned) ||
    Boolean(o.furgonetka_undelivered)
  ) {
    return true;
  }
  const label = (o.furgonetka_status_label || "").toLowerCase();
  return /nieudan[ae]\s+(?:pr[oó]b[ay]\s+)?dor[eę]cz|niedor[eę]cz|nieodebr|awizo|zwrot|decyzja\s+o\s+zwrocie|do\s+nadawcy|undeliver|delivery[\s_-]?problem/.test(
    label,
  );
}

/** Paczka odebrana/doręczona: flaga sync albo etykieta kuriera (nie stary fulfillment_status). */
function isPackageDelivered(o: FulfillmentOrder): boolean {
  // Nieopłacona / nowa paczka nie może być „Doręczone” (chroni przed flagami z 1. wysyłki).
  if (!o.furgonetka_paid) return false;
  if (o.furgonetka_returned || o.furgonetka_undelivered) return false;
  if (o.furgonetka_delivered) return true;
  const label = (o.furgonetka_status_label || "").toLowerCase();
  if (/nieodebr|niedor[eę]cz|zwrot/.test(label)) return false;
  return /dor[eę]czon|dostarczon|odebran[aoy]|wydan[oa]\s+odbior/.test(label);
}

/** Numer ponownej wysyłki (2., 3. …); null dla pierwszej. */
function shipmentAttemptBadge(o: FulfillmentOrder): string | null {
  const n = Number(o.shipment_attempt || 1);
  if (!Number.isFinite(n) || n <= 1) return null;
  return `${Math.floor(n)}. wysyłka`;
}

function attentionBadgeLabel(o: FulfillmentOrder): string {
  if (o.fulfillment_status === "complaint_open") return "Reklamacja";
  if (o.fulfillment_status === "ready_to_reship") return "Do ponownej wysyłki";
  if (o.fulfillment_status === "return_closed") return "Zwrot OK";
  if (o.fulfillment_status === "withdrawal") return "Odstąpienie";
  if (o.furgonetka_returned || /zwrot|decyzja\s+o\s+zwrocie|do\s+nadawcy/i.test(o.furgonetka_status_label || "")) {
    return "Zwrot";
  }
  if (o.furgonetka_undelivered) return "Niedoręczenie";
  const label = (o.furgonetka_status_label || "").toLowerCase();
  if (/nieudan[ae]|niedor[eę]cz|nieodebr|awizo|undeliver|delivery[\s_-]?problem/.test(label)) {
    return "Niedoręczenie";
  }
  return "Zwrot";
}

function formatOrderDate(iso: string | null | undefined): string {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    // EasyCart często: "2026-07-07 20:50:33"
    const m = String(iso).match(/^(\d{4}-\d{2}-\d{2})/);
    if (m) {
      const [y, mo, day] = m[1].split("-");
      return `${day}.${mo}.${y}`;
    }
    return String(iso).slice(0, 16);
  }
  return d.toLocaleDateString("pl-PL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/** Data doręczenia, zwrotu albo odstąpienia. */
function outcomeEventDate(o: FulfillmentOrder): { label: string; value: string } | null {
  const isWithdrawal = o.fulfillment_status === "withdrawal";
  const isReturn =
    o.fulfillment_status === "return_open" ||
    o.fulfillment_status === "return_closed" ||
    Boolean(o.furgonetka_returned) ||
    Boolean(o.furgonetka_undelivered);
  const isDelivered = o.fulfillment_status === "delivered";
  if (!isReturn && !isDelivered && !isWithdrawal) return null;
  const raw = o.furgonetka_status_at || o.updated_at || null;
  let label = "Dostawa";
  if (isWithdrawal) label = "Odstąpienie";
  else if (isReturn) {
    label = o.furgonetka_undelivered && !o.furgonetka_returned ? "Niedoręcz." : "Zwrot";
  }
  return {
    label,
    value: formatOrderDate(raw),
  };
}

const FURGONETKA_SYNC_BATCH = 8;

function booksLabel(n: number): string {
  const q = Math.max(1, n);
  if (q === 1) return "1 książka";
  const mod10 = q % 10;
  const mod100 = q % 100;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${q} książki`;
  return `${q} książek`;
}

function formatAddressBlock(
  street?: string | null,
  postcode?: string | null,
  city?: string | null,
): string {
  const line2 = [postcode, city].filter(Boolean).join(" ");
  return [street, line2].filter(Boolean).join(", ") || "—";
}

function ActionSpinner({ className }: { className?: string }) {
  return <Loader2 className={cn("h-4 w-4 animate-spin", className)} aria-hidden />;
}

function PaczkomatMatchStatusBadge({
  order,
  className,
}: {
  order: Pick<
    FulfillmentOrder,
    | "paczkomat_name"
    | "furgonetka_package_id"
    | "paczkomat_match_status"
    | "paczkomat_match_label"
    | "paczkomat_match_error"
    | "paczkomat_match_at"
    | "paczkomat_delivery_address"
  >;
  className?: string;
}) {
  const status = order.paczkomat_match_status;
  if (!status || !order.paczkomat_name || order.furgonetka_package_id) return null;
  // Po ręcznym/udanym dopasowaniu adresu nie pokazuj „Nie dopasowano / ponowienie”.
  if (
    order.paczkomat_delivery_address?.resolved &&
    (status === "failed" || status === "pending" || status === "outside_queue")
  ) {
    return null;
  }

  const label = order.paczkomat_match_label ?? status;
  const title = [
    order.paczkomat_match_error,
    order.paczkomat_match_at
      ? `Ostatnia próba: ${new Date(order.paczkomat_match_at).toLocaleString("pl-PL")}`
      : null,
  ]
    .filter(Boolean)
    .join("\n");

  const tone: Record<PaczkomatMatchStatus, string> = {
    running: "border-cyan-600/50 bg-cyan-100 text-cyan-950",
    pending: "border-muted-foreground/35 text-muted-foreground",
    success: "border-success/50 bg-success/10 text-success",
    failed: "border-l-4 border-l-orange-500 border-orange-200 bg-white text-orange-950",
    outside_queue: "border-dashed border-muted-foreground/40 text-muted-foreground",
  };

  return (
    <Badge
      variant="outline"
      className={cn("mt-1 max-w-full whitespace-normal text-[10px] font-normal leading-tight", tone[status], className)}
      title={title || undefined}
    >
      {status === "running" && <ActionSpinner className="mr-1 inline h-3 w-3" />}
      {label}
    </Badge>
  );
}

type DetailProgressStatus = "done" | "active" | "pending" | "skipped";

interface DetailProgressStep {
  label: string;
  detail?: string;
  status: DetailProgressStatus;
}

function DetailProgressList({
  steps,
  elapsedSec,
  compact,
}: {
  steps: DetailProgressStep[];
  elapsedSec?: number;
  compact?: boolean;
}) {
  if (!steps.length) return null;
  return (
    <div className={cn(compact ? "space-y-2" : "mx-auto w-full max-w-sm space-y-3")}>
      {!compact && elapsedSec != null && elapsedSec >= 2 && (
        <p className="text-center text-xs text-muted-foreground">Trwa {elapsedSec} s…</p>
      )}
      <ol className={cn("space-y-2", compact ? "text-sm" : "text-sm")} aria-label="Postęp operacji">
        {steps.map((step, index) => (
          <li
            key={`${step.label}-${index}`}
            className={cn(
              "flex gap-2.5",
              step.status === "pending" && "opacity-45",
              step.status === "skipped" && "opacity-30",
            )}
          >
            <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
              {step.status === "done" ? (
                <Check className="h-4 w-4 text-emerald-600 dark:text-emerald-400" aria-hidden />
              ) : step.status === "active" ? (
                <ActionSpinner className="text-brand h-4 w-4" />
              ) : (
                <span
                  className="inline-block h-2.5 w-2.5 rounded-full border-2 border-muted-foreground/35"
                  aria-hidden
                />
              )}
            </span>
            <div className="min-w-0">
              <p
                className={cn(
                  "leading-snug",
                  step.status === "active" && "font-medium text-foreground",
                  step.status === "done" && "text-foreground",
                )}
              >
                {step.label}
              </p>
              {step.detail && (
                <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground break-words">
                  {step.detail}
                </p>
              )}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function truncateProgressText(text: string, max = 72): string {
  const t = text.trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1)}…`;
}

function BookQtyBadge({ qty, large }: { qty: number; large?: boolean }) {
  const n = Math.max(1, qty);
  return (
    <Badge
      variant={n > 1 ? "default" : "secondary"}
      className={cn(
        "tabular-nums font-bold",
        large ? "text-base px-3 py-1" : "text-sm px-2.5 py-0.5",
        n > 1 && "bg-brand text-brand-foreground",
      )}
      title={booksLabel(n)}
    >
      {n}×
    </Badge>
  );
}

const DEFAULT_PARCEL: FurgonetkaParcelSettings = {
  parcel_type: "dox",
  length_cm: 24,
  width_cm: 16,
  height_cm: 4,
  weight_kg: 0.5,
  value_pln: 49,
  content: "ksiazka",
};

export default function Wysylka() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [detailId, setDetailId] = useState<number | null>(null);
  const [batchQuote, setBatchQuote] = useState<{ total: number; results: unknown[] } | null>(null);
  const [detailQuote, setDetailQuote] = useState<{
    price: number | null;
    error?: string;
    success?: string;
    editUrl?: string;
    pickupManualFix?: boolean;
  } | null>(null);
  const [parcelForm, setParcelForm] = useState<FurgonetkaParcelSettings>(DEFAULT_PARCEL);
  const [parcelSaved, setParcelSaved] = useState(false);
  const [parcelPanelOpen, setParcelPanelOpen] = useState(false);
  const [pricePanelOpen, setPricePanelOpen] = useState(false);
  const [importBanner, setImportBanner] = useState<FulfillmentImportResult | null>(null);
  const [furgonetkaSyncProgress, setFurgonetkaSyncProgress] = useState<{
    done: number;
    total: number;
    phase: "running" | "done";
  } | null>(null);
  const csvInputRef = useRef<HTMLInputElement>(null);
  const furgonetkaSyncStartedRef = useRef(false);

  const { data: summary } = useQuery({
    queryKey: ["fulfillment-summary"],
    queryFn: api.fulfillmentSummary,
  });
  const { data: importStatus } = useQuery({
    queryKey: ["fulfillment-import-status"],
    queryFn: api.fulfillmentImportStatus,
    staleTime: 30_000,
  });

  const invalidateFulfillment = () => {
    qc.invalidateQueries({ queryKey: ["fulfillment-summary"] });
    qc.invalidateQueries({ queryKey: ["fulfillment-orders"] });
    qc.invalidateQueries({ queryKey: ["fulfillment-orders-attention"] });
    qc.invalidateQueries({ queryKey: ["fulfillment-import-status"] });
  };
  const { data: fkConfig } = useQuery({
    queryKey: ["furgonetka-config"],
    queryFn: api.furgonetkaConfig,
  });

  useEffect(() => {
    if (fkConfig?.parcel) {
      setParcelForm(fkConfig.parcel);
    }
  }, [fkConfig?.parcel]);

  /** Wycena tabeli kurierów: zawsze nadanie w placówce (bez odbioru kuriera). */
  const selectedShipmentMode = "placowka";

  const {
    data: fkServices,
    isLoading: servicesLoading,
    isFetching: servicesFetching,
    isError: servicesError,
    refetch: refetchServices,
  } = useQuery({
    queryKey: ["furgonetka-services", selectedShipmentMode],
    queryFn: () => api.furgonetkaServices(selectedShipmentMode),
    enabled: Boolean(fkConfig?.configured && !fkConfig.sandbox_mode && selectedShipmentMode),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

  const selectedCourierId = fkConfig?.selected_courier?.service_id ?? fkServices?.selected?.service_id ?? null;
  const courierOptions = fkServices?.items ?? [];
  const selectedCourierMeta = useMemo(
    () => courierOptions.find((c) => c.service_id === selectedCourierId),
    [courierOptions, selectedCourierId],
  );
  const inpostCourier = useMemo(
    () => courierOptions.find((c) => (c.service || "").toLowerCase() === "inpost"),
    [courierOptions],
  );
  const couriersLoading = servicesLoading || (servicesFetching && courierOptions.length === 0);

  const batchShipOptions = useMemo(() => {
    const svc = fkConfig?.selected_courier?.service || fkServices?.selected?.service;
    const isPocztaDefault = (svc || "").toLowerCase() === "poczta";
    if (!selectedCourierId || isPocztaDefault) {
      return {};
    }
    return { service_id: selectedCourierId, service: svc };
  }, [selectedCourierId, fkConfig?.selected_courier?.service, fkServices?.selected?.service]);
  const {
    data: listData,
    isLoading: listLoading,
    isFetching: listFetching,
  } = useQuery({
    queryKey: ["fulfillment-orders", q, statusFilter],
    queryFn: () =>
      api.fulfillmentOrders({
        q: q || undefined,
        status: statusFilter || undefined,
        limit: 200,
      }),
    placeholderData: keepPreviousData,
    staleTime: 5_000,
    refetchInterval: (query) => {
      const data = query.state.data;
      const rows = data?.items ?? [];
      if (data?.paczkomat_background?.running_order_id) return 8_000;
      if (
        rows.some(
          (o) =>
            o.paczkomat_match_status === "running" || o.paczkomat_match_status === "pending",
        )
      ) {
        return 12_000;
      }
      return false;
    },
  });
  const listInitialLoading = listLoading && !listData;
  const rawItems = listData?.items ?? [];

  const { data: attentionData } = useQuery({
    queryKey: ["fulfillment-orders-attention"],
    queryFn: () => api.fulfillmentOrders({ attention: true, limit: 50 }),
    staleTime: 5_000,
  });
  const attentionItems = attentionData?.items ?? [];
  const attentionIds = useMemo(() => new Set(attentionItems.map((o) => o.id)), [attentionItems]);

  const items = useMemo(() => {
    if (statusFilter) return rawItems;
    return rawItems.filter((o) => !attentionIds.has(o.id) && !isAttentionOrder(o));
  }, [rawItems, attentionIds, statusFilter]);

  const {
    data: detailFetched,
    isFetching: detailFetching,
    isPending: detailPending,
    isError: detailLoadError,
  } = useQuery({
    queryKey: ["fulfillment-order", detailId],
    queryFn: () => api.fulfillmentOrder(detailId!),
    enabled: detailId != null,
    refetchInterval: (query) => {
      const d = query.state.data;
      if (
        d?.paczkomat_match_pending &&
        !d?.furgonetka_package_id &&
        d?.paczkomat_name
      ) {
        return 15_000;
      }
      return false;
    },
  });

  const detailListRow = useMemo(
    () =>
      detailId != null
        ? items.find((o) => o.id === detailId) ||
          attentionItems.find((o) => o.id === detailId) ||
          rawItems.find((o) => o.id === detailId)
        : undefined,
    [items, attentionItems, rawItems, detailId],
  );
  /** Wiersz z listy od razu; pełny GET w tle. */
  const detail = (detailFetched ?? detailListRow) as FulfillmentOrderDetail | undefined;

  useEffect(() => {
    setEditingOrder(false);
    setEditForm({});
    setPaczkomatCandidates([]);
    setSelectedPaczkomatCode("");
    setPaczkomatResolveMsg(null);
    setDetailLoadStartedAt(detailId != null ? Date.now() : null);
    setDetailElapsedSec(0);
  }, [detailId]);

  const startOrderEdit = (order: FulfillmentOrderDetail) => {
    setEditForm({
      customer_name: order.customer_name ?? "",
      customer_email: order.customer_email ?? "",
      phone: order.phone ?? "",
      shipping_name: order.shipping_name ?? order.customer_name ?? "",
      shipping_street: order.shipping_street ?? "",
      shipping_postal_code: order.shipping_postal_code ?? "",
      shipping_city: order.shipping_city ?? "",
      dedication: order.dedication ?? "",
      paczkomat_name: order.paczkomat_name ?? "",
      internal_notes: order.internal_notes ?? "",
    });
    setEditingOrder(true);
  };

  const saveOrderEdit = () => {
    if (!detail) return;
    const patch: FulfillmentOrderPatch = {};
    const keys = [
      "customer_name",
      "customer_email",
      "phone",
      "shipping_name",
      "shipping_street",
      "shipping_postal_code",
      "shipping_city",
      "dedication",
      "paczkomat_name",
      "internal_notes",
    ] as const;
    for (const key of keys) {
      const next = (editForm[key] ?? "").trim();
      const prev = (detail[key] ?? "").trim();
      if (next !== prev) {
        patch[key] = next;
      }
    }
    if (Object.keys(patch).length === 0) {
      setEditingOrder(false);
      return;
    }
    patchMut.mutate(
      { id: detail.id, patch },
      { onSuccess: () => setEditingOrder(false) },
    );
  };

  const syncMut = useMutation({
    mutationFn: api.fulfillmentSync,
    onSuccess: (data) => {
      setImportBanner(data);
      invalidateFulfillment();
    },
  });

  const furgonetkaStatusMut = useMutation({
    mutationFn: (ids?: number[]) => api.furgonetkaSyncStatus(ids),
    onSuccess: (data) => {
      setImportBanner({
        status: "success",
        message: `Furgonetka: zsynchronizowano ${data.synced ?? 0} paczek · opłacone: ${data.paid ?? 0} · nadane: ${data.dispatched ?? 0}`,
      });
      invalidateFulfillment();
    },
  });

  const runFurgonetkaStatusSync = async (
    ids: number[] | undefined,
    opts?: { silent?: boolean },
  ) => {
    const silent = Boolean(opts?.silent);
    let targetIds = ids;
    if (!targetIds || targetIds.length === 0) {
      const candidates = await api.furgonetkaSyncCandidates();
      targetIds = candidates.ids ?? [];
    }
    if (targetIds.length === 0) {
      setFurgonetkaSyncProgress(null);
      if (!silent) {
        setImportBanner({
          status: "success",
          message: "Furgonetka: brak paczek do synchronizacji (poza doręczonymi).",
        });
      }
      return;
    }
    setFurgonetkaSyncProgress({ done: 0, total: targetIds.length, phase: "running" });
    let synced = 0;
    let paid = 0;
    let dispatched = 0;
    try {
      for (let i = 0; i < targetIds.length; i += FURGONETKA_SYNC_BATCH) {
        const batch = targetIds.slice(i, i + FURGONETKA_SYNC_BATCH);
        const data = await api.furgonetkaSyncStatus(batch);
        synced += data.synced ?? 0;
        paid += data.paid ?? 0;
        dispatched += data.dispatched ?? 0;
        setFurgonetkaSyncProgress({
          done: Math.min(i + batch.length, targetIds.length),
          total: targetIds.length,
          phase: "running",
        });
      }
      invalidateFulfillment();
      setFurgonetkaSyncProgress({
        done: targetIds.length,
        total: targetIds.length,
        phase: "done",
      });
      if (!silent) {
        setImportBanner({
          status: "success",
          message: `Furgonetka: zsynchronizowano ${synced} paczek · opłacone: ${paid} · nadane: ${dispatched}`,
        });
      }
      window.setTimeout(() => setFurgonetkaSyncProgress(null), 2500);
    } catch (err) {
      setFurgonetkaSyncProgress(null);
      if (!silent) {
        const msg = err instanceof Error ? err.message : "Błąd sync Furgonetka";
        setImportBanner({ status: "error", message: msg });
      }
    }
  };

  const uploadCsvMut = useMutation({
    mutationFn: api.fulfillmentUploadCsv,
    onSuccess: (data) => {
      setImportBanner(data);
      invalidateFulfillment();
    },
  });

  const handleCsvFile = (file: File | undefined) => {
    if (!file) return;
    uploadCsvMut.mutate(file);
  };

  const patchMut = useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: Parameters<typeof api.fulfillmentPatch>[1] }) =>
      api.fulfillmentPatch(id, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["fulfillment-summary"] });
      qc.invalidateQueries({ queryKey: ["fulfillment-orders"] });
      if (detailId) qc.invalidateQueries({ queryKey: ["fulfillment-order", detailId] });
    },
  });

  const additionalShipmentMut = useMutation({
    mutationFn: (id: number) => api.fulfillmentAdditionalShipment(id),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["fulfillment-summary"] });
      qc.invalidateQueries({ queryKey: ["fulfillment-orders"] });
      if (detailId) qc.invalidateQueries({ queryKey: ["fulfillment-order", detailId] });
      const order = data.order;
      if (order) {
        startOrderEdit(order);
      }
    },
  });

  const requestAdditionalShipment = (order: FulfillmentOrderDetail) => {
    const pid = order.furgonetka_package_id || "?";
    const ok = window.confirm(
      `Nowa wysyłka dla #${order.easycart_order_id}?\n\n` +
        `Odłączymy paczkę Furgonetka ${pid} (zostanie w historii zdarzeń), ` +
        `status wróci do „Nowe”. Potem zaktualizuj adres/paczkomat i zleć ponownie.`,
    );
    if (!ok) return;
    additionalShipmentMut.mutate(order.id);
  };

  const paczkomatResolveMut = useMutation({
    mutationFn: ({
      id,
      apply,
      point_code,
      service,
    }: {
      id: number;
      apply?: boolean;
      point_code?: string;
      service?: string;
    }) => api.fulfillmentResolvePaczkomat(id, { apply, point_code, service }),
    onSuccess: (data) => {
      setPaczkomatCandidates(data.candidates ?? []);
      if (data.point_code) {
        setSelectedPaczkomatCode(data.point_code);
      } else if (data.candidates?.length === 1) {
        setSelectedPaczkomatCode(data.candidates[0].code);
      }
      if (data.error) {
        setPaczkomatResolveMsg(data.error);
      } else if (data.applied) {
        setPaczkomatResolveMsg(`Zapisano kod ${data.point_code}.`);
      } else if (data.resolved && data.point_code) {
        setPaczkomatResolveMsg(`Znaleziono: ${data.point_code} (${data.service ?? "?"})`);
      } else if (data.candidates?.length) {
        setPaczkomatResolveMsg(`Znaleziono ${data.candidates.length} kandydatów - wybierz i zastosuj kod.`);
      } else {
        setPaczkomatResolveMsg("Nie znaleziono automatu w mapie Furgonetki.");
      }
      qc.invalidateQueries({ queryKey: ["fulfillment-summary"] });
      qc.invalidateQueries({ queryKey: ["fulfillment-orders"] });
      if (detailId) qc.invalidateQueries({ queryKey: ["fulfillment-order", detailId] });
    },
    onError: (err: Error) => {
      setPaczkomatResolveMsg(err.message || "Błąd wyszukiwania automatu.");
    },
  });

  const shipInpostMut = useMutation({
    mutationFn: (id: number) =>
      api.furgonetkaCreateBatch({
        ids: [id],
        confirm: true,
        service: "inpost",
        service_id: inpostCourier?.service_id,
      }),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["fulfillment-summary"] });
      qc.invalidateQueries({ queryKey: ["fulfillment-orders"] });
      if (detailId != null) {
        qc.invalidateQueries({ queryKey: ["fulfillment-order", detailId] });
      }
      const row = data.results?.[0] as
        | {
            status?: string;
            message?: string;
            price_pln?: number;
            package_id?: string;
            furgonetka_edit_url?: string;
          }
        | undefined;
      if (row?.status === "success") {
        setDetailQuote({
          price: row.price_pln ?? null,
          error: undefined,
          success: row.message || `Paczka ${row.package_id || ""} utworzona (InPost).`,
          editUrl: row.furgonetka_edit_url,
        });
      } else {
        setDetailQuote({
          price: null,
          error: row?.message || "Nie udało się zlecić InPost",
        });
      }
    },
  });

  const shipMut = useMutation({
    mutationFn: (args: number[] | { ids: number[]; forcePoczta?: boolean }) => {
      const ids = Array.isArray(args) ? args : args.ids;
      const forcePoczta = !Array.isArray(args) && Boolean(args.forcePoczta);
      return api.furgonetkaCreateBatch({
        ids,
        confirm: true,
        ...(forcePoczta ? { service: "poczta" } : batchShipOptions),
      });
    },
    onSuccess: (data) => {
      setBatchQuote(null);
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ["fulfillment-summary"] });
      qc.invalidateQueries({ queryKey: ["fulfillment-orders"] });
      if (detailId != null) {
        qc.invalidateQueries({ queryKey: ["fulfillment-order", detailId] });
      }
      const row = data.results?.find(
        (r) => detailId != null && (r as { order_id?: number }).order_id === detailId,
      ) as {
        status?: string;
        message?: string;
        price_pln?: number;
        package_id?: string;
        furgonetka_edit_url?: string;
        pickup_manual_fix_required?: boolean;
      } | undefined;
      if (row?.status === "success") {
        setDetailQuote({
          price: row.price_pln ?? null,
          error: undefined,
          success: row.message || `Paczka ${row.package_id || ""} utworzona.`,
          editUrl: row.furgonetka_edit_url,
          pickupManualFix: row.pickup_manual_fix_required,
        });
      } else if (row?.status === "error" || row?.status === "skipped") {
        setDetailQuote({ price: null, error: row.message || "Nie udało się zlecić wysyłki" });
      }
    },
    onError: (err: Error) => {
      setDetailQuote({ price: null, error: err.message || "Błąd tworzenia paczki" });
    },
  });

  const quoteMut = useMutation({
    mutationFn: (ids: number[]) =>
      api.furgonetkaCreateBatch({ ids, quote_only: true, ...batchShipOptions }),
    onSuccess: (data, ids) => {
      if (detailId != null) {
        qc.invalidateQueries({ queryKey: ["fulfillment-order", detailId] });
      }
      if (ids.length === 1 && detailId != null && ids[0] === detailId) {
        const row = data.results?.[0] as { status?: string; price_pln?: number; message?: string } | undefined;
        if (row?.status === "success") {
          setDetailQuote({
            price: row.price_pln ?? null,
            error: undefined,
            success: row.message || "Paczka poprawna — można zlecić wysyłkę.",
          });
        } else {
          setDetailQuote({
            price: null,
            error: row?.message || (row?.status === "skipped" ? "Pominięto wycenę" : "Nie udało się wycenić"),
          });
        }
      } else {
        setBatchQuote({ total: data.total_price_pln ?? 0, results: data.results });
      }
    },
    onError: (err: Error) => {
      setDetailQuote({ price: null, error: err.message || "Błąd wyceny" });
    },
  });

  const sandboxMut = useMutation({
    mutationFn: (sandbox: boolean) => api.furgonetkaSetSandbox(sandbox),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["furgonetka-config"] });
      qc.invalidateQueries({ queryKey: ["furgonetka-services"] });
    },
  });

  const courierMut = useMutation({
    mutationFn: (c: { service_id: number; service: string; label?: string }) => api.furgonetkaSetCourier(c),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["furgonetka-config"] });
      qc.invalidateQueries({ queryKey: ["furgonetka-services"] });
    },
  });

  const parcelMut = useMutation({
    mutationFn: () => api.furgonetkaSetParcelSettings(parcelForm),
    onSuccess: () => {
      setParcelSaved(true);
      qc.invalidateQueries({ queryKey: ["furgonetka-config"] });
      qc.invalidateQueries({ queryKey: ["furgonetka-services"] });
      window.setTimeout(() => setParcelSaved(false), 2500);
    },
  });

  const [labelBusy, setLabelBusy] = useState(false);
  const [pocztexFormBusy, setPocztexFormBusy] = useState(false);
  const [editingOrder, setEditingOrder] = useState(false);
  const [editForm, setEditForm] = useState<FulfillmentOrderPatch>({});
  const [paczkomatCandidates, setPaczkomatCandidates] = useState<PaczkomatResolveCandidate[]>([]);
  const [selectedPaczkomatCode, setSelectedPaczkomatCode] = useState<string>("");
  const [paczkomatResolveMsg, setPaczkomatResolveMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!detail?.paczkomat_delivery_address?.candidates?.length) return;
    if (paczkomatCandidates.length > 0) return;
    setPaczkomatCandidates(detail.paczkomat_delivery_address.candidates);
    if (detail.paczkomat_delivery_address.point_code) {
      setSelectedPaczkomatCode(detail.paczkomat_delivery_address.point_code);
    } else if (detail.paczkomat_delivery_address.candidates.length === 1) {
      setSelectedPaczkomatCode(detail.paczkomat_delivery_address.candidates[0].code);
    }
  }, [detail, paczkomatCandidates.length]);

  const [detailPreflightBusy, setDetailPreflightBusy] = useState(false);
  const [detailLoadStartedAt, setDetailLoadStartedAt] = useState<number | null>(null);
  const [detailElapsedSec, setDetailElapsedSec] = useState(0);

  const handleLabel = async (orderId: number, mode: "print" | "download") => {
    setLabelBusy(true);
    try {
      const blob = await api.furgonetkaLabelBlob(orderId);
      const url = URL.createObjectURL(blob);
      if (mode === "print") {
        const win = window.open(url, "_blank");
        if (win) {
          win.addEventListener("load", () => win.print());
        } else {
          window.open(url, "_blank");
        }
      } else {
        const a = document.createElement("a");
        a.href = url;
        a.download = `etykieta-${orderId}.pdf`;
        a.click();
      }
      window.setTimeout(() => URL.revokeObjectURL(url), 120_000);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Błąd pobierania etykiety";
      window.alert(msg);
    } finally {
      setLabelBusy(false);
    }
  };

  const handlePocztexForeignForm = async (orderId: number) => {
    setPocztexFormBusy(true);
    try {
      const blob = await api.pocztexForeignFormBlob(orderId);
      const url = URL.createObjectURL(blob);
      const win = window.open(url, "_blank");
      if (!win) {
        window.alert("Zablokowano okno podręczne. Odblokuj popup i spróbuj ponownie.");
      }
      window.setTimeout(() => URL.revokeObjectURL(url), 180_000);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Błąd generowania blankietu";
      window.alert(msg);
    } finally {
      setPocztexFormBusy(false);
    }
  };

  const paczkomatMatchSummary = useMemo(() => {
    const counts = {
      running: 0,
      pending: 0,
      success: 0,
      failed: 0,
      outside_queue: 0,
    };
    for (const o of items) {
      const s = o.paczkomat_match_status;
      if (s && s in counts) counts[s as keyof typeof counts] += 1;
    }
    return counts;
  }, [items]);

  const detailInitialLoading =
    detailId != null && !detail && (detailPending || detailFetching);

  const s: FulfillmentSummary = summary ?? {
    to_ship: 0,
    new: 0,
    ready_to_reship: 0,
    shipment_created: 0,
    dedication: 0,
    packing: 0,
    ready_to_ship: 0,
    preparing: 0,
    shipped_today: 0,
    with_dedication: 0,
    complaints: 0,
    active_orders: 0,
    total_books: 0,
    by_status: {},
  };

  const toggleSelect = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectedIds = useMemo(() => Array.from(selected), [selected]);

  const furgonetkaApiReady = Boolean(
    fkConfig?.configured &&
      fkConfig.user_auth_configured &&
      !fkConfig.sandbox_mode,
  );

  useEffect(() => {
    if (!furgonetkaApiReady || furgonetkaSyncStartedRef.current) return;
    furgonetkaSyncStartedRef.current = true;
    void runFurgonetkaStatusSync(undefined, { silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- raz przy wejściu na stronę gdy API gotowe
  }, [furgonetkaApiReady]);

  const furgonetkaBlockedReason = fkConfig?.sandbox_mode
    ? "Wyłącz Sandbox (przycisk u góry) — produkcyjne API działa z Twoim loginem Furgonetki."
    : !fkConfig?.user_auth_configured
      ? "Brak loginu Furgonetki w konfiguracji serwera."
      : !fkConfig?.configured
        ? "Brak CLIENT_ID/SECRET w .env."
        : null;

  const preflightPaczkomatForShip = async (orderId: number): Promise<void> => {
    if (!detail?.paczkomat_name || detail.paczkomat_delivery_address?.resolved) return;
    const data = await api.fulfillmentResolvePaczkomat(orderId, { apply: false });
    setPaczkomatCandidates(data.candidates ?? []);
    if (data.point_code) {
      setSelectedPaczkomatCode(data.point_code);
    } else if (data.candidates?.length === 1) {
      setSelectedPaczkomatCode(data.candidates[0].code);
    }
    if (data.confidence === "high" && data.point_code) {
      await api.fulfillmentResolvePaczkomat(orderId, {
        apply: true,
        point_code: data.point_code,
        service: data.service ?? undefined,
      });
      await qc.invalidateQueries({ queryKey: ["fulfillment-order", orderId] });
      return;
    }
    if (!data.resolved && (data.candidates?.length ?? 0) > 0) {
      throw new Error("Wybierz paczkomat z listy i kliknij „Zastosuj kod”, potem ponów wycenę.");
    }
    if (data.error && !(data.candidates?.length ?? 0)) {
      throw new Error(data.error);
    }
  };

  const handleDetailQuote = async () => {
    if (!detail) return;
    setDetailPreflightBusy(true);
    setDetailQuote({ price: null, error: undefined, success: undefined });
    try {
      await preflightPaczkomatForShip(detail.id);
      quoteMut.mutate([detail.id]);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Nie udało się przygotować wyceny.";
      setDetailQuote({ price: null, error: msg });
    } finally {
      setDetailPreflightBusy(false);
    }
  };

  const handleDetailShip = async () => {
    if (!detail) return;
    setDetailPreflightBusy(true);
    setDetailQuote({ price: null, error: undefined, success: undefined });
    try {
      await preflightPaczkomatForShip(detail.id);
      shipMut.mutate(
        detail.paczkomat_name
          ? [detail.id]
          : { ids: [detail.id], forcePoczta: true },
      );
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Nie udało się utworzyć paczki.";
      setDetailQuote({ price: null, error: msg });
    } finally {
      setDetailPreflightBusy(false);
    }
  };

  const detailPanelBusy =
    detailInitialLoading ||
    detailPreflightBusy ||
    quoteMut.isPending ||
    shipMut.isPending ||
    paczkomatResolveMut.isPending ||
    shipInpostMut.isPending ||
    patchMut.isPending ||
    additionalShipmentMut.isPending;

  useEffect(() => {
    if (!detailPanelBusy || detailLoadStartedAt == null) {
      setDetailElapsedSec(0);
      return;
    }
    const tick = () =>
      setDetailElapsedSec(Math.max(0, Math.floor((Date.now() - detailLoadStartedAt) / 1000)));
    tick();
    const timer = window.setInterval(tick, 500);
    return () => window.clearInterval(timer);
  }, [detailPanelBusy, detailLoadStartedAt]);

  const detailProgressSteps = useMemo((): DetailProgressStep[] => {
    const orderNo =
      detail?.easycart_order_id ?? detailListRow?.easycart_order_id ?? (detailId != null ? "…" : "");
    const customer = detail?.customer_name ?? detailListRow?.customer_name;
    const city = detail?.shipping_city ?? detailListRow?.shipping_city;
    const paczkomat = detail?.paczkomat_name ?? detailListRow?.paczkomat_name;
    const courier =
      detail?.paczkomat_courier_label ?? detailListRow?.paczkomat_courier_label;
    const paczkomatResolved = Boolean(detail?.paczkomat_delivery_address?.resolved);
    const paczkomatBgPending = Boolean(
      (detail?.paczkomat_match_status === "pending" ||
        detail?.paczkomat_match_status === "running" ||
        detail?.paczkomat_match_pending) &&
        !detail?.furgonetka_package_id &&
        paczkomat &&
        !paczkomatResolved,
    );
    const needsPaczkomatLookup = Boolean(
      paczkomat && furgonetkaApiReady && !paczkomatResolved && !paczkomatBgPending,
    );

    const steps: DetailProgressStep[] = [
      { label: "Otwieranie panelu zamówienia", status: "done" },
    ];

    if (detailInitialLoading) {
      steps.push({
        label: `Pobieranie zamówienia #${orderNo}`,
        detail: [customer, city].filter(Boolean).join(" · ") || undefined,
        status: "active",
      });
      if (paczkomat) {
        steps.push({
          label: "Przygotowanie wyszukiwania punktu odbioru",
          detail: truncateProgressText(paczkomat),
          status: "pending",
        });
      }
      steps.push({
        label: "Gotowe do edycji i wysyłki",
        status: "pending",
      });
      return steps;
    }

    if (detailLoadError) {
      steps.push({
        label: "Pobieranie zamówienia nie powiodło się",
        status: "active",
      });
      return steps;
    }

    steps.push({
      label: `Zamówienie #${orderNo} wczytane`,
      detail: [customer, city].filter(Boolean).join(" · ") || undefined,
      status: "done",
    });

    if (paczkomatResolveMut.isPending) {
      steps.push({
        label: "Wyszukiwanie punktu w mapie Furgonetki",
        detail: [
          paczkomat ? `Punkt: ${truncateProgressText(paczkomat, 56)}` : null,
          courier ? `Kurier: ${courier}` : null,
          city ? `Miasto: ${city}` : null,
        ]
          .filter(Boolean)
          .join(" · "),
        status: "active",
      });
    } else if (paczkomatBgPending && detail?.paczkomat_match_status === "running") {
      steps.push({
        label: "Wyszukiwanie automatu w tle",
        detail: truncateProgressText(paczkomat ?? ""),
        status: "active",
      });
    } else if (paczkomatBgPending) {
      steps.push({
        label: "Punkt odbioru wymaga dopasowania",
        detail: truncateProgressText(paczkomat ?? ""),
        status: "pending",
      });
    } else if (needsPaczkomatLookup && furgonetkaApiReady) {
      steps.push({
        label: "Punkt odbioru wymaga dopasowania",
        detail: truncateProgressText(paczkomat ?? ""),
        status: "pending",
      });
    } else if (paczkomat && paczkomatResolved) {
      steps.push({
        label: "Punkt odbioru rozpoznany",
        detail: detail?.paczkomat_delivery_address?.point_code
          ? `Kod: ${detail.paczkomat_delivery_address.point_code}`
          : undefined,
        status: "done",
      });
    } else if (paczkomat && !furgonetkaApiReady) {
      steps.push({
        label: "Automatyczne wyszukiwanie niedostępne",
        detail: furgonetkaBlockedReason ?? "Furgonetka nie jest gotowa (sandbox / brak loginu).",
        status: "skipped",
      });
    }

    if (detailPreflightBusy) {
      steps.push({
        label: "Sprawdzanie paczkomatu przed wysyłką",
        detail: "Walidacja punktu odbioru…",
        status: "active",
      });
    } else if (quoteMut.isPending) {
      steps.push({
        label: "Wycena paczki u kuriera",
        detail: courier ? `Usługa: ${courier}` : "Obliczanie stawki…",
        status: "active",
      });
    } else if (shipMut.isPending) {
      steps.push({
        label: "Tworzenie paczki w Furgonetce",
        detail: "Validate → utworzenie zlecenia…",
        status: "active",
      });
    } else if (shipInpostMut.isPending) {
      steps.push({
        label: "Zlecanie InPost Kurier → adres",
        status: "active",
      });
    } else if (patchMut.isPending) {
      steps.push({
        label: "Zapisywanie zmian w zamówieniu",
        status: "active",
      });
    } else if (additionalShipmentMut.isPending) {
      steps.push({
        label: "Przygotowanie nowej wysyłki",
        detail: "Odłączanie starej paczki Furgonetka…",
        status: "active",
      });
    } else if (detailFetching && detail) {
      steps.push({
        label: "Odświeżanie danych zamówienia",
        status: "active",
      });
    }

    return steps;
  }, [
    detail,
    detailListRow,
    detailId,
    detailInitialLoading,
    detailLoadError,
    detailFetching,
    detailPreflightBusy,
    furgonetkaApiReady,
    furgonetkaBlockedReason,
    paczkomatResolveMut.isPending,
    quoteMut.isPending,
    shipMut.isPending,
    shipInpostMut.isPending,
    patchMut.isPending,
    additionalShipmentMut.isPending,
  ]);

  const detailBusyLabel = detailInitialLoading
    ? "Ładowanie zamówienia…"
    : detailPreflightBusy
      ? "Szukam automatu…"
      : paczkomatResolveMut.isPending
        ? "Wyszukiwanie paczkomatu w mapie Furgonetki…"
        : quoteMut.isPending
          ? "Wycena paczki…"
          : shipMut.isPending
            ? "Tworzenie paczki w Furgonetce…"
            : shipInpostMut.isPending
              ? "Zlecanie InPost…"
              : additionalShipmentMut.isPending
                ? "Przygotowanie nowej wysyłki…"
                : patchMut.isPending
                  ? "Zapisywanie zmian…"
                  : detailFetching
                    ? "Odświeżanie zamówienia…"
                    : "";

  const detailBusySubtext = useMemo((): string | undefined => {
    const paczkomat = detail?.paczkomat_name ?? detailListRow?.paczkomat_name;
    const city = detail?.shipping_city ?? detailListRow?.shipping_city;
    const courier =
      detail?.paczkomat_courier_label ?? detailListRow?.paczkomat_courier_label;

    if (paczkomatResolveMut.isPending) {
      return [
        paczkomat ? truncateProgressText(paczkomat, 64) : null,
        courier,
        city ? `miasto: ${city}` : null,
      ]
        .filter(Boolean)
        .join(" · ");
    }
    if (detailPreflightBusy) {
      return paczkomat ? truncateProgressText(paczkomat, 64) : "Walidacja punktu odbioru…";
    }
    if (quoteMut.isPending && courier) return `Usługa: ${courier}`;
    if (shipMut.isPending) return "Validate → utworzenie zlecenia w Furgonetce";
    return undefined;
  }, [
    detail,
    detailListRow,
    detailPreflightBusy,
    paczkomatResolveMut.isPending,
    quoteMut.isPending,
    shipMut.isPending,
  ]);

  const exportCsv = () => {
    if (!selectedIds.length) return;
    window.open(api.fulfillmentExportCsvUrl(selectedIds), "_blank");
  };

  return (
    <div>
      <PageHeader
        title="Wysyłka"
        subtitle="Książka papierowa — EasyCart + Furgonetka"
        actions={
          <div className="flex flex-wrap gap-2">
            {furgonetkaApiReady && (
              <span
                className="inline-flex h-9 max-w-md items-center rounded-md border border-input bg-muted/40 px-3 text-xs text-muted-foreground"
                title="Tryb wybierany automatycznie per zamówienie"
              >
                Paczkomat w zamówieniu → InPost. Bez paczkomatu → nadanie na poczcie.
              </span>
            )}
            {fkConfig?.configured && (
              <Button
                variant={fkConfig.sandbox_mode ? "default" : "outline"}
                size="sm"
                className={cn(
                  fkConfig.sandbox_mode && "bg-amber-600 hover:bg-amber-700 text-white border-amber-600",
                )}
                onClick={() => sandboxMut.mutate(!fkConfig.sandbox_mode)}
                disabled={sandboxMut.isPending}
                title={fkConfig.sandbox_mode ? fkConfig.api_base : "Przełącz na sandbox Furgonetki"}
              >
                <FlaskConical className="mr-2 h-4 w-4" />
                {fkConfig.sandbox_mode ? "Sandbox ON" : "Sandbox OFF"}
              </Button>
            )}
            <input
              ref={csvInputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => {
                handleCsvFile(e.target.files?.[0]);
                e.target.value = "";
              }}
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => csvInputRef.current?.click()}
              disabled={uploadCsvMut.isPending}
              title="Pobierz eksport z EasyCart (Zamówienia → Eksport CSV) i wgraj tutaj"
            >
              <FileDown className={cn("mr-2 h-4 w-4", uploadCsvMut.isPending && "animate-pulse")} />
              Wgraj CSV
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => syncMut.mutate()}
              disabled={syncMut.isPending}
              title="Przetwarza webhooki i CSV już na serwerze — nie pobiera nowego eksportu z EasyCart"
            >
              <RefreshCw className={cn("mr-2 h-4 w-4", syncMut.isPending && "animate-spin")} />
              Sync (CSV + webhook)
            </Button>
            {furgonetkaApiReady && (
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  void runFurgonetkaStatusSync(
                    selectedIds.length > 0 ? selectedIds : undefined,
                  )
                }
                disabled={furgonetkaSyncProgress?.phase === "running"}
                title="Pobierz z Furgonetki: opłacone / nadane (zaznaczone lub wszystkie z paczką, bez doręczonych)"
              >
                <RefreshCw
                  className={cn(
                    "mr-2 h-4 w-4",
                    furgonetkaSyncProgress?.phase === "running" && "animate-spin",
                  )}
                />
                Status Furgonetka
              </Button>
            )}
            {selectedIds.length > 0 && (
              <>
                <Button variant="outline" size="sm" onClick={exportCsv}>
                  <Download className="mr-2 h-4 w-4" />
                  CSV ({selectedIds.length})
                </Button>
                {furgonetkaApiReady && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => quoteMut.mutate(selectedIds)}
                      disabled={quoteMut.isPending}
                    >
                      Wycena ({selectedIds.length})
                    </Button>
                    <Button size="sm" onClick={() => shipMut.mutate(selectedIds)} disabled={shipMut.isPending}>
                      <Send className="mr-2 h-4 w-4" />
                      Furgonetka ({selectedIds.length})
                    </Button>
                  </>
                )}
              </>
            )}
          </div>
        }
      />

      {fkConfig?.sandbox_mode && (
        <OpCallout variant="warning" title="Tryb testowy Furgonetka" className="mb-4">
          <p>
            API: <code className="text-xs">{fkConfig.api_base}</code>.
            {!fkConfig.sandbox_credentials_separate && (
              <>
                {" "}
                Produkcyjne CLIENT_ID nie działają w sandboxie. Potrzebna osobna aplikacja OAuth (
                <code className="text-xs">FURGONETKA_SANDBOX_CLIENT_ID/SECRET</code>).
              </>
            )}
            {!fkConfig.user_auth_configured && (
              <>
                {" "}
                Do zlecania paczek ustaw też login Furgonetki w{" "}
                <code className="text-xs">FURGONETKA_USERNAME/PASSWORD</code>.
              </>
            )}
          </p>
        </OpCallout>
      )}

      {fkConfig?.configured && !fkConfig.sandbox_mode && !fkConfig.user_auth_configured && (
        <OpCallout variant="warning" title="Brak loginu Furgonetki" className="mb-4">
          Ustaw <code>FURGONETKA_USERNAME/PASSWORD</code>. Bez tego zlecanie paczek może zwrócić błąd autoryzacji.
        </OpCallout>
      )}

      {!fkConfig?.configured && (
        <OpCallout variant="warning" title="Furgonetka nie skonfigurowana" className="mb-4">
          Użyj eksportu CSV albo uzupełnij FURGONETKA_CLIENT_ID/SECRET w .env.
        </OpCallout>
      )}

      {batchQuote && (
        <div className={cn("mb-4 space-y-2 px-4 py-3 text-sm", OP_SURFACE.params.box)}>
          <div>
            Szacunkowa wycena batch: <strong>{batchQuote.total.toFixed(2)} PLN</strong>
            <Button variant="ghost" size="sm" className="ml-2" onClick={() => setBatchQuote(null)}>
              Zamknij
            </Button>
          </div>
          {(batchQuote.results as { status?: string; message?: string; order_id?: number }[] | undefined)
            ?.filter((r) => r.status === "error" || r.status === "skipped")
            .slice(0, 8)
            .map((r) => (
              <p key={r.order_id ?? r.message} className="text-destructive text-xs">
                #{r.order_id}: {r.message}
              </p>
            ))}
        </div>
      )}

      {furgonetkaApiReady && (
        <Card className="mb-4">
          <CardHeader className="pb-3">
            <button
              type="button"
              className="flex w-full items-start justify-between gap-2 text-left"
              onClick={() => setParcelPanelOpen((v) => !v)}
              aria-expanded={parcelPanelOpen}
            >
              <div className="min-w-0">
                <CardTitle className="text-base flex items-center gap-2">
                  {parcelPanelOpen ? (
                    <ChevronDown className="h-4 w-4 shrink-0" />
                  ) : (
                    <ChevronRight className="h-4 w-4 shrink-0" />
                  )}
                  Parametry przesyłki
                </CardTitle>
                {!parcelPanelOpen && (
                  <p className="text-xs text-muted-foreground mt-1 pl-6">
                    {parcelForm.parcel_type === "dox" ? "Koperta (dox)" : "Paczka"} ·{" "}
                    {parcelForm.length_cm}×{parcelForm.width_cm}×{parcelForm.height_cm} cm ·{" "}
                    {parcelForm.weight_kg} kg · {parcelForm.value_pln} PLN
                  </p>
                )}
                {parcelPanelOpen && (
                  <p className="text-xs text-muted-foreground mt-1 pl-6">
                    Wymiary, waga i wartość używane przy wycenie i zleceniu wysyłki, zapamiętywane na serwerze.
                    Koperta (dox): max 35×25×5 cm (domyślnie 24×16×4).
                  </p>
                )}
              </div>
            </button>
          </CardHeader>
          {parcelPanelOpen && (
          <CardContent>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <label className="grid gap-1 text-sm">
                <span className="text-muted-foreground">Typ</span>
                <select
                  className="h-10 rounded-md border border-input bg-background px-3"
                  value={parcelForm.parcel_type}
                  onChange={(e) =>
                    setParcelForm((p) => ({
                      ...p,
                      parcel_type: e.target.value as FurgonetkaParcelSettings["parcel_type"],
                    }))
                  }
                >
                  <option value="dox">Koperta (dox)</option>
                  <option value="package">Paczka</option>
                </select>
              </label>
              <label className="grid gap-1 text-sm">
                <span className="text-muted-foreground">Długość (cm)</span>
                <Input
                  type="number"
                  min={1}
                  step={0.1}
                  value={parcelForm.length_cm}
                  onChange={(e) => setParcelForm((p) => ({ ...p, length_cm: Number(e.target.value) }))}
                />
              </label>
              <label className="grid gap-1 text-sm">
                <span className="text-muted-foreground">Szerokość (cm)</span>
                <Input
                  type="number"
                  min={1}
                  step={0.1}
                  value={parcelForm.width_cm}
                  onChange={(e) => setParcelForm((p) => ({ ...p, width_cm: Number(e.target.value) }))}
                />
              </label>
              <label className="grid gap-1 text-sm">
                <span className="text-muted-foreground">Wysokość (cm)</span>
                <Input
                  type="number"
                  min={0.1}
                  step={0.1}
                  value={parcelForm.height_cm}
                  onChange={(e) => setParcelForm((p) => ({ ...p, height_cm: Number(e.target.value) }))}
                />
              </label>
              <label className="grid gap-1 text-sm">
                <span className="text-muted-foreground">Waga (kg)</span>
                <Input
                  type="number"
                  min={0.01}
                  step={0.01}
                  value={parcelForm.weight_kg}
                  onChange={(e) => setParcelForm((p) => ({ ...p, weight_kg: Number(e.target.value) }))}
                />
              </label>
              <label className="grid gap-1 text-sm">
                <span className="text-muted-foreground">Wartość (PLN)</span>
                <Input
                  type="number"
                  min={0}
                  step={1}
                  value={parcelForm.value_pln}
                  onChange={(e) => setParcelForm((p) => ({ ...p, value_pln: Number(e.target.value) }))}
                />
              </label>
              <label className="grid gap-1 text-sm sm:col-span-2">
                <span className="text-muted-foreground">Zawartość (opis)</span>
                <Input
                  value={parcelForm.content}
                  onChange={(e) => setParcelForm((p) => ({ ...p, content: e.target.value }))}
                />
              </label>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Button size="sm" onClick={() => parcelMut.mutate()} disabled={parcelMut.isPending}>
                {parcelMut.isPending ? "Zapisywanie…" : "Zapisz parametry"}
              </Button>
              {parcelSaved && (
                <span className="text-sm text-emerald-600 dark:text-emerald-400">Zapisano</span>
              )}
              {parcelMut.isError && (
                <span className="text-sm text-destructive">
                  {parcelMut.error instanceof Error ? parcelMut.error.message : "Błąd zapisu"}
                </span>
              )}
            </div>
          </CardContent>
          )}
        </Card>
      )}

      {furgonetkaApiReady && selectedShipmentMode && (
        <Card className="mb-4">
          <CardHeader className="pb-3">
            <button
              type="button"
              className="flex w-full items-start justify-between gap-2 text-left"
              onClick={() => setPricePanelOpen((v) => !v)}
              aria-expanded={pricePanelOpen}
            >
              <div className="min-w-0 flex-1">
                <CardTitle className="text-base flex items-center gap-2">
                  {pricePanelOpen ? (
                    <ChevronDown className="h-4 w-4 shrink-0" />
                  ) : (
                    <ChevronRight className="h-4 w-4 shrink-0" />
                  )}
                  Cena za przesyłkę
                </CardTitle>
                {!pricePanelOpen && (
                  <p className="text-xs text-muted-foreground mt-1 pl-6">
                    {selectedCourierMeta
                      ? `${selectedCourierMeta.label || selectedCourierMeta.service}${
                          selectedCourierMeta.price_pln != null
                            ? ` · ${selectedCourierMeta.price_pln.toFixed(2)} PLN`
                            : ""
                        }`
                      : "Wybierz przewoźnika po rozwinięciu"}
                    {fkConfig?.drop_point?.label ? ` · ${fkConfig.drop_point.label}` : ""}
                  </p>
                )}
                {pricePanelOpen && (
                  <p className="text-xs text-muted-foreground mt-1 pl-6">
                    Ceny brutto (z VAT) · wycena przy nadaniu w placówce (Pocztex itd.)
                    {fkConfig?.drop_point?.label && (
                      <>
                        {" "}
                        · Punkt nadania: <strong>{fkConfig.drop_point.label}</strong>
                      </>
                    )}
                  </p>
                )}
              </div>
              {couriersLoading && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground shrink-0">
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  Wycena…
                </div>
              )}
            </button>
          </CardHeader>
          {pricePanelOpen && (
          <CardContent>
            {servicesError && (
              <Button variant="outline" size="sm" className="mb-3" onClick={() => refetchServices()}>
                Odśwież wycenę
              </Button>
            )}
            {courierOptions.length === 0 && !couriersLoading ? (
              <p className="text-sm text-muted-foreground">
                {fkServices?.message || "Brak ofert dla wybranego trybu nadania."}
              </p>
            ) : (
              <div className="overflow-x-auto pb-1">
                <table className="min-w-full border-collapse text-sm">
                  <thead>
                    <tr>
                      <th className="border-b border-border px-2 py-2 text-left text-xs font-medium text-muted-foreground w-36">
                        Wybierz przewoźnika
                      </th>
                      {courierOptions.map((c) => {
                        const selected = selectedCourierId === c.service_id;
                        const hasQuote = c.price_pln != null;
                        return (
                          <th key={c.service_id} className="border-b border-r border-border p-0 align-top font-normal">
                            <button
                              type="button"
                              disabled={courierMut.isPending}
                              onClick={() =>
                                courierMut.mutate({
                                  service_id: c.service_id,
                                  service: c.service,
                                  label: c.label,
                                })
                              }
                              className={cn(
                                "h-full w-full min-w-[9rem] px-2 py-3 text-left transition-colors hover:bg-muted/50",
                                selected && "bg-brand/10 ring-1 ring-inset ring-brand/40",
                                !hasQuote && !selected && "bg-muted/20",
                              )}
                            >
                              <div className="text-sm font-semibold">{c.label || c.service}</div>
                              {hasQuote ? (
                                <div className="mt-1 text-lg font-bold">{c.price_pln!.toFixed(2)} zł</div>
                              ) : (
                                <div className="mt-1 text-xs text-muted-foreground">Brak wyceny</div>
                              )}
                            </button>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border-b border-border px-2 py-2 text-xs font-medium text-muted-foreground">
                        Ubezpieczenie w cenie
                      </td>
                      {courierOptions.map((c) => (
                        <td key={`ins-${c.service_id}`} className="border-b border-r border-border px-2 py-2">
                          {c.insurance_label || "—"}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td className="px-2 py-2 text-xs font-medium text-muted-foreground">Średni czas doręczenia</td>
                      {courierOptions.map((c) => (
                        <td key={`time-${c.service_id}`} className="border-r border-border px-2 py-2 align-top">
                          {c.delivery_time_label || (c.available_for_mode ? "—" : null)}
                          {c.unavailable_reason && (
                            <p className="text-xs text-destructive leading-snug">{c.unavailable_reason}</p>
                          )}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
                {selectedCourierMeta?.price_pln == null && selectedCourierId != null && (
                  <p className="mt-3 text-xs text-muted-foreground">
                    Wybrany kurier ({selectedCourierMeta?.label || selectedCourierMeta?.service}) nie ma wyceny z API
                    — możesz go i tak wybrać i zlecić wysyłkę; dokładna cena pojawi się w panelu Furgonetka.
                  </p>
                )}
              </div>
            )}
          </CardContent>
          )}
        </Card>
      )}

      {(importBanner?.message || importStatus?.csv) && (
        <div
          className={cn(
            "mt-4 rounded-lg border px-4 py-3 text-sm",
            importBanner?.inserted ? "border-emerald-500/40 bg-emerald-500/10" : "border-amber-500/40 bg-amber-500/10",
          )}
        >
          {importBanner?.message && <p className="font-medium">{importBanner.message}</p>}
          {!importBanner?.message && importStatus?.csv?.exists && (
            <p className="text-muted-foreground">
              Eksport CSV na serwerze
              {importStatus.csv.row_count != null ? ` (${importStatus.csv.row_count} wierszy)` : ""}
              {importStatus.csv.mtime_iso
                ? ` · plik: ${new Date(importStatus.csv.mtime_iso).toLocaleString("pl-PL")}`
                : ""}
              {importStatus.gaps?.csv_newest_order_id
                ? ` · najnowsze ID w pliku: ${importStatus.gaps.csv_newest_order_id}`
                : ""}
              {importStatus.db.max_ordered_at
                ? ` · w bazie (lista): ${new Date(importStatus.db.max_ordered_at).toLocaleString("pl-PL")}`
                : ""}
            </p>
          )}
          {(importStatus?.gaps?.zrealizowane_missing_in_db ?? 0) > 0 && (
            <p className="mt-2 font-medium text-amber-900 dark:text-amber-100">
              W pliku CSV jest {importStatus!.gaps!.zrealizowane_missing_in_db} zrealizowanych zamówień, których nie ma w
              panelu — kliknij „Wgraj CSV” (ten sam plik) lub odśwież eksport z EasyCart.
            </p>
          )}
          <p className="text-muted-foreground mt-1 text-xs">
            <strong>Sync EasyCart</strong> nie pobiera listy z chmury — tylko przetwarza webhooki (port 8787) i plik CSV
            już leżący na serwerze. Nowe zakupy: eksport w EasyCart → <strong>Wgraj CSV</strong>. Importuje wszystkie
            zamówienia z pliku (pomija tylko duplikaty po ID). Webhooki produkcyjne:{" "}
            {importStatus?.webhook_events_total ?? 0} zdarzeń w logu.
          </p>
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8">
        <Kpi title="Książki (pipeline)" value={s.total_books ?? s.to_ship ?? 0} icon={Package} />
        <Kpi title="Nowe" value={s.new ?? 0} icon={Package} />
        <Kpi title="Ponowna wysyłka" value={s.ready_to_reship ?? 0} icon={RefreshCw} />
        <Kpi title="Furgonetka" value={s.shipment_created ?? 0} icon={Send} />
        <Kpi title="Dedykacja" value={s.dedication ?? s.preparing ?? 0} icon={Heart} />
        <Kpi title="Pakowanie" value={s.packing ?? 0} icon={Package} />
        <Kpi title="Do wysyłki" value={s.ready_to_ship ?? 0} icon={Truck} />
        <Kpi title="Nadane dziś" value={s.shipped_today} icon={Truck} />
      </div>

      {furgonetkaSyncProgress && (
        <div className="mt-4 rounded-lg border border-brand/30 bg-brand/5 px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
            <span className="font-medium">
              {furgonetkaSyncProgress.phase === "done"
                ? `Zsynchronizowano ${furgonetkaSyncProgress.total}`
                : `Sync Furgonetka: ${furgonetkaSyncProgress.done} / ${furgonetkaSyncProgress.total}`}
            </span>
            {furgonetkaSyncProgress.phase === "running" && (
              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                <ActionSpinner className="h-3 w-3" />
                w tle
              </span>
            )}
          </div>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-brand transition-[width] duration-300"
              style={{
                width: `${
                  furgonetkaSyncProgress.total > 0
                    ? Math.round(
                        (100 * furgonetkaSyncProgress.done) / furgonetkaSyncProgress.total,
                      )
                    : 0
                }%`,
              }}
            />
          </div>
        </div>
      )}

      {attentionItems.length > 0 && (
        <Card className="mt-5 border-rose-500/40">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base text-rose-800 dark:text-rose-200">
              <AlertTriangle className="h-4 w-4" />
              Do uwagi: zwroty / niedoręczenia / odstąpienia ({attentionItems.length})
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              Zamówienia zwrócone, niedoręczone, w reklamacji lub z odstąpieniem od umowy (nie duplikują się w
              tabeli poniżej przy filtrze „Wszystkie”).
            </p>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="p-2">ID</th>
                  <th className="p-2">Zamówione</th>
                  <th className="p-2">Dostawa / zwrot</th>
                  <th className="p-2">Klient</th>
                  <th className="p-2">Miasto</th>
                  <th className="p-2">Status</th>
                  <th className="p-2">Furgonetka</th>
                </tr>
              </thead>
              <tbody>
                {attentionItems.map((o) => {
                  const outcome = outcomeEventDate(o);
                  return (
                  <tr
                    key={o.id}
                    className={cn(
                      "border-b border-border/50 cursor-pointer border-l-2 border-l-rose-500",
                      "bg-rose-500/15 hover:bg-rose-500/25",
                      detailId === o.id && "ring-1 ring-inset ring-rose-500/40",
                    )}
                    onClick={() => {
                      setDetailId(o.id);
                      setDetailQuote(null);
                    }}
                  >
                    <td className="p-2 font-mono text-xs">{o.easycart_order_id}</td>
                    <td className="p-2 whitespace-nowrap text-xs text-muted-foreground">
                      {formatOrderDate(o.ordered_at)}
                    </td>
                    <td className="p-2 whitespace-nowrap text-xs">
                      {outcome ? (
                        <span title={outcome.label}>
                          <span className="text-muted-foreground">{outcome.label}: </span>
                          {outcome.value}
                        </span>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td className="p-2">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="font-medium">{o.customer_name}</span>
                        <Badge
                          variant="outline"
                          className="border-rose-500/50 bg-rose-500/15 text-rose-900 dark:text-rose-100"
                        >
                          {attentionBadgeLabel(o)}
                        </Badge>
                        {shipmentAttemptBadge(o) && (
                          <Badge
                            variant="outline"
                            className="border-blue-600/50 bg-blue-100 text-blue-950"
                          >
                            {shipmentAttemptBadge(o)}
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="p-2">{o.shipping_city || "-"}</td>
                    <td className="p-2">
                      <Badge
                        variant="outline"
                        className={cn(
                          o.fulfillment_status === "ready_to_reship" &&
                            "border-blue-600/50 bg-blue-100 text-blue-950",
                        )}
                      >
                        {STATUS_LABELS[o.fulfillment_status] ?? o.fulfillment_status}
                      </Badge>
                    </td>
                    <td className="p-2 text-xs text-muted-foreground max-w-[220px] truncate">
                      {o.furgonetka_status_label || o.furgonetka_package_id || "-"}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <Card className="mt-5">
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3">
          <div className="space-y-1">
            <CardTitle className="flex items-center gap-2">
              Zamówienia
              {listFetching && !listInitialLoading && (
                <span className="inline-flex items-center gap-1 text-xs font-normal text-muted-foreground">
                  <ActionSpinner className="h-3 w-3" />
                  odświeżanie
                </span>
              )}
            </CardTitle>
            {(listData?.paczkomat_background?.running_order_id ||
              paczkomatMatchSummary.running +
                paczkomatMatchSummary.pending +
                paczkomatMatchSummary.failed >
                0) && (
              <div className="text-xs text-muted-foreground space-y-0.5">
                {listData?.paczkomat_background?.running_easycart_order_id && (
                  <p className="font-medium text-blue-800 dark:text-blue-200">
                    W tle: wyszukiwanie automatu dla #
                    {listData.paczkomat_background.running_easycart_order_id}
                  </p>
                )}
                <p>
                  Mapa Furgonetki (TOP {listData?.paczkomat_background?.max_orders ?? 20}):{" "}
                  {paczkomatMatchSummary.success > 0 && (
                    <span className="text-emerald-700 dark:text-emerald-300">
                      {paczkomatMatchSummary.success} dopasowane
                    </span>
                  )}
                  {paczkomatMatchSummary.pending > 0 && (
                    <span>
                      {paczkomatMatchSummary.success > 0 ? " · " : ""}
                      {paczkomatMatchSummary.pending} w kolejce
                    </span>
                  )}
                  {paczkomatMatchSummary.failed > 0 && (
                    <span className="text-amber-800 dark:text-amber-200">
                      {(paczkomatMatchSummary.success > 0 || paczkomatMatchSummary.pending > 0)
                        ? " · "
                        : ""}
                      {paczkomatMatchSummary.failed} nieudane
                    </span>
                  )}
                  {paczkomatMatchSummary.outside_queue > 0 && (
                    <span>
                      {(paczkomatMatchSummary.success +
                        paczkomatMatchSummary.pending +
                        paczkomatMatchSummary.failed >
                      0)
                        ? " · "
                        : ""}
                      {paczkomatMatchSummary.outside_queue} poza kolejką
                    </span>
                  )}
                </p>
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <Input
              placeholder="Szukaj…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-48"
            />
            <select
              className="h-10 rounded-md border border-input bg-background px-3 text-sm"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">Wszystkie statusy</option>
              {FILTER_STATUSES.map((k) => (
                <option key={k} value={k}>
                  {STATUS_LABELS[k] ?? k}
                </option>
              ))}
            </select>
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {listInitialLoading ? (
            <p className="text-muted-foreground text-sm">Ładowanie…</p>
          ) : items.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              Brak zamówień — wgraj świeży eksport CSV z EasyCart (przycisk „Wgraj CSV”) lub Sync, jeśli plik na serwerze
              jest aktualny. Na liście widać tylko status EasyCart „Zrealizowane”.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="p-2 w-8" />
                  <th className="p-2">ID</th>
                  <th className="p-2">Zamówione</th>
                  <th className="p-2">Dostawa / zwrot</th>
                  <th className="p-2">Klient</th>
                  <th className="p-2 text-center">Książki</th>
                  <th className="p-2">Miasto</th>
                  <th className="p-2">Dedykacja</th>
                  <th className="p-2">Paczkomat</th>
                  <th className="p-2">Dopasowanie</th>
                  <th className="p-2">Kurier</th>
                  <th className="p-2">Status</th>
                  <th className="p-2">Furgonetka</th>
                </tr>
              </thead>
              <tbody>
                {items.map((o) => {
                  const outcome = outcomeEventDate(o);
                  return (
                  <tr
                    key={o.id}
                    className={cn(
                      "border-b border-border/50 hover:bg-muted/30 cursor-pointer",
                      detailId === o.id && "bg-muted/50 ring-1 ring-inset ring-brand/35",
                      o.shipping_is_foreign &&
                        "bg-violet-200/90 hover:bg-violet-200 border-l-4 border-l-violet-700",
                    )}
                    onClick={() => {
                      setDetailId(o.id);
                      setDetailQuote(null);
                    }}
                  >
                    <td className="p-2" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selected.has(o.id)}
                        onChange={() => toggleSelect(o.id)}
                      />
                    </td>
                    <td className="p-2 font-mono text-xs">{o.easycart_order_id}</td>
                    <td className="p-2 whitespace-nowrap text-xs text-muted-foreground">
                      {formatOrderDate(o.ordered_at)}
                    </td>
                    <td className="p-2 whitespace-nowrap text-xs">
                      {outcome ? (
                        <span title={outcome.label}>
                          <span className="text-muted-foreground">{outcome.label}: </span>
                          {outcome.value}
                        </span>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td className="p-2">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="font-medium">{o.customer_name}</span>
                        {shipmentAttemptBadge(o) && (
                          <Badge
                            variant="outline"
                            className="border-blue-600/50 bg-blue-100 text-blue-950"
                            title="Ponowna wysyłka (po zwrocie / niedoręczeniu)"
                          >
                            {shipmentAttemptBadge(o)}
                          </Badge>
                        )}
                        {o.shipping_is_foreign && (
                          <Badge
                            variant="outline"
                            className="border-violet-700/60 bg-violet-100 font-semibold text-violet-950"
                            title="Adres wysyłki poza Polską"
                          >
                            Zagranica
                          </Badge>
                        )}
                      </div>
                      {(o.customer_books_total ?? o.book_quantity ?? 1) > (o.book_quantity ?? 1) && (
                        <div className="mt-0.5 text-xs text-muted-foreground">
                          Łącznie u klienta: {booksLabel(o.customer_books_total ?? 1)}
                        </div>
                      )}
                    </td>
                    <td className="p-2 text-center">
                      <BookQtyBadge qty={o.book_quantity ?? 1} />
                    </td>
                    <td className="p-2">{o.shipping_city || "—"}</td>
                    <td className="p-2 max-w-[260px] align-top">
                      {o.dedication ? (
                        <span
                          className="line-clamp-3 whitespace-pre-wrap break-words text-sm"
                          title={o.dedication}
                        >
                          {o.dedication}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="p-2 max-w-[160px] truncate">{o.paczkomat_name || "—"}</td>
                    <td className="p-2 max-w-[180px] align-top">
                      {o.paczkomat_name && !o.furgonetka_package_id ? (
                        <PaczkomatMatchStatusBadge order={o} />
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="p-2">
                      {o.paczkomat_courier_label ? (
                        <Badge variant="secondary" title={o.paczkomat_courier ?? undefined}>
                          {o.paczkomat_courier_label}
                        </Badge>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="p-2">
                      <Badge
                        variant="outline"
                        className={cn(
                          o.fulfillment_status === "ready_to_reship" &&
                            "border-blue-600/50 bg-blue-100 text-blue-950",
                        )}
                      >
                        {STATUS_LABELS[o.fulfillment_status] ?? o.fulfillment_status}
                      </Badge>
                    </td>
                    <td className="p-2">
                      {o.furgonetka_package_id ? (
                        <div className="flex flex-col gap-1">
                          {shipmentAttemptBadge(o) && (
                            <Badge
                              variant="outline"
                              className="border-blue-600/50 bg-blue-100 text-blue-950"
                            >
                              {shipmentAttemptBadge(o)}
                            </Badge>
                          )}
                          <Badge variant="success">Zlecono</Badge>
                          {o.furgonetka_status_synced_at != null ? (
                            <>
                              <Badge variant={o.furgonetka_paid ? "success" : "warning"}>
                                {o.furgonetka_paid ? "Opłacone" : "Do opłacenia"}
                              </Badge>
                              {o.furgonetka_paid && isPackageDelivered(o) ? (
                                <Badge variant="success" title={o.furgonetka_status_label ?? undefined}>
                                  Doręczone
                                </Badge>
                              ) : o.furgonetka_paid && o.furgonetka_dispatched ? (
                                <Badge variant="outline" title={o.furgonetka_status_label ?? undefined}>
                                  Nadane
                                </Badge>
                              ) : null}
                            </>
                          ) : (
                            <Badge variant="warning">Do opłacenia</Badge>
                          )}
                        </div>
                      ) : shipmentAttemptBadge(o) ? (
                        <div className="flex flex-col gap-1">
                          <Badge
                            variant="outline"
                            className="border-blue-600/50 bg-blue-100 text-blue-950"
                          >
                            {shipmentAttemptBadge(o)}
                          </Badge>
                          <Badge variant="warning">Do zlecenia</Badge>
                        </div>
                      ) : o.furgonetka_expected ? (
                        <Badge variant="warning">EasyCart?</Badge>
                      ) : (
                        <Badge variant="secondary">—</Badge>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {detailId != null && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={() => setDetailId(null)}>
          <motion.div
            initial={{ x: 320 }}
            animate={{ x: 0 }}
            className="h-full w-full max-w-md overflow-y-auto bg-card p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-lg font-semibold">
                  #{detail?.easycart_order_id ?? detailListRow?.easycart_order_id ?? "…"}
                </h2>
                {shipmentAttemptBadge(detail ?? detailListRow!) && (
                  <Badge
                    variant="outline"
                    className="border-blue-600/50 bg-blue-100 text-blue-950"
                  >
                    {shipmentAttemptBadge(detail ?? detailListRow!)}
                  </Badge>
                )}
                {(detail?.shipping_is_foreign || detailListRow?.shipping_is_foreign) && (
                  <Badge
                    variant="outline"
                    className="border-violet-700/60 bg-violet-100 font-semibold text-violet-950"
                  >
                    Zagranica
                  </Badge>
                )}
              </div>
              <div className="flex shrink-0 gap-1">
                {detail && !editingOrder ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => startOrderEdit(detail)}
                    title="Edytuj dane zamówienia"
                    disabled={detailPanelBusy}
                  >
                    <Pencil className="mr-1 h-3.5 w-3.5" />
                    Edytuj
                  </Button>
                ) : null}
                <Button variant="ghost" size="icon" onClick={() => setDetailId(null)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
            {detail && shipmentAttemptBadge(detail) && !detail.furgonetka_package_id && (
              <p className={cn("mt-2 px-2 py-1.5 text-xs", OP_SURFACE.params.box)}>
                To jest <strong>{shipmentAttemptBadge(detail)}</strong> tego zamówienia
                (poprzednia paczka w historii zdarzeń). Zaktualizuj adres/paczkomat i zleć w Furgonetce.
              </p>
            )}
            {detail?.furgonetka_package_id && (
              <div className="mt-2 space-y-2">
                {shipmentAttemptBadge(detail) && (
                  <p className={cn("px-2 py-1.5 text-xs", OP_SURFACE.params.box)}>
                    To jest <strong>{shipmentAttemptBadge(detail)}</strong> tego zamówienia.
                  </p>
                )}
                <p className={cn("px-2 py-1.5 text-xs", OP_SURFACE.review.box)}>
                  Paczka już w Furgonetce — zmiany adresu nie aktualizują istniejącego zlecenia.
                  Żeby wysłać ponownie (np. po zwrocie, na inny adres), użyj „Nowa wysyłka”.
                </p>
                {!editingOrder && (
                  <Button
                    variant="secondary"
                    size="sm"
                    className="w-full"
                    disabled={detailPanelBusy}
                    onClick={() => requestAdditionalShipment(detail)}
                  >
                    {additionalShipmentMut.isPending ? (
                      <>
                        <ActionSpinner className="mr-2" />
                        Przygotowuję…
                      </>
                    ) : (
                      <>
                        <RefreshCw className="mr-2 h-3.5 w-3.5" />
                        Nowa wysyłka
                      </>
                    )}
                  </Button>
                )}
                {additionalShipmentMut.isError && (
                  <p className="text-xs text-destructive">
                    {(additionalShipmentMut.error as Error)?.message ||
                      "Nie udało się przygotować nowej wysyłki"}
                  </p>
                )}
              </div>
            )}
            {detailPanelBusy && detailBusyLabel && !detailInitialLoading && (
              <div
                className="mt-3 rounded-md border border-brand/35 bg-brand/10 px-3 py-2.5 text-sm text-foreground"
                role="status"
                aria-live="polite"
              >
                <div className="flex items-start gap-2">
                  <ActionSpinner className="text-brand mt-0.5 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium leading-snug">{detailBusyLabel}</p>
                    {detailBusySubtext && (
                      <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground break-words">
                        {detailBusySubtext}
                      </p>
                    )}
                    {detailElapsedSec >= 2 && (
                      <p className="mt-1 text-xs text-muted-foreground">Trwa {detailElapsedSec} s…</p>
                    )}
                  </div>
                </div>
                {detailProgressSteps.some((s) => s.status === "active") && (
                  <div className="mt-3 border-t border-brand/20 pt-3">
                    <DetailProgressList steps={detailProgressSteps} compact />
                  </div>
                )}
              </div>
            )}
            {detailInitialLoading ? (
              <div className="mt-8 flex flex-col items-center justify-center gap-5 py-12 text-muted-foreground">
                <ActionSpinner className="h-9 w-9 text-brand" />
                <p className="text-sm font-medium text-foreground">
                  {detailBusyLabel || "Ładowanie zamówienia…"}
                </p>
                <DetailProgressList
                  steps={detailProgressSteps}
                  elapsedSec={detailElapsedSec}
                />
              </div>
            ) : detailLoadError ? (
              <div className="mt-8 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-4 text-sm text-destructive">
                Nie udało się wczytać zamówienia. Zamknij panel i spróbuj ponownie.
              </div>
            ) : detail ? (
              <>
            {editingOrder ? (
              <OrderEditForm
                form={editForm}
                onChange={setEditForm}
                onCancel={() => setEditingOrder(false)}
                onSave={saveOrderEdit}
                saving={patchMut.isPending}
              />
            ) : (
              <>
            <p className="mt-1 text-sm text-muted-foreground">{detail.customer_name}</p>
            <p className="text-sm">{detail.customer_email}</p>
            <div className="mt-3 flex flex-wrap items-center gap-3 rounded-lg border border-brand/30 bg-brand/5 px-3 py-2">
              <span className="text-sm font-medium">To zamówienie:</span>
              <BookQtyBadge qty={detail.book_quantity ?? 1} large />
              <span className="text-sm text-muted-foreground">{booksLabel(detail.book_quantity ?? 1)}</span>
              {(detail.customer_books_total ?? 0) > (detail.book_quantity ?? 1) && (
                <span className="text-sm">
                  · Łącznie u tego e-mail:{" "}
                  <strong>{booksLabel(detail.customer_books_total ?? 1)}</strong>
                </span>
              )}
              {(detail.amount ?? 0) > 0 && (
                <span className="text-xs text-muted-foreground ml-auto">
                  {detail.amount?.toFixed(2)} {detail.currency?.toUpperCase() || "PLN"}
                </span>
              )}
            </div>
                <div className="mt-3 space-y-3 text-sm">
                  <div
                    className={cn(
                      "rounded-lg border px-3 py-2",
                      detail.shipping_is_foreign
                        ? "border-dashed border-violet-600/50 bg-violet-50"
                        : "border-border/80 bg-muted/30",
                    )}
                  >
                    <p className="font-medium text-foreground">Adres klienta (EasyCart)</p>
                    {detail.shipping_name && detail.shipping_name !== detail.customer_name && (
                      <p className="mt-1 text-muted-foreground">{detail.shipping_name}</p>
                    )}
                    <p className="mt-1">
                      {formatAddressBlock(
                        detail.shipping_street,
                        detail.shipping_postal_code,
                        detail.shipping_city,
                      )}
                    </p>
                    <p className="mt-1 text-muted-foreground">Tel: {detail.phone || "—"}</p>
                    {detail.phone &&
                      (detail.phone.replace(/\D/g, "").length > 9 ||
                        /^\+?(?:00)?(?:49|44|43|33|31|32|39)/.test(
                          detail.phone.replace(/[\s()-]/g, ""),
                        )) && (
                        <p className="mt-2 text-xs text-amber-800 dark:text-amber-200">
                          {detail.paczkomat_name
                            ? "InPost Paczkomat wymaga polskiego numeru (9 cyfr) do SMS. W Edytuj wpisz numer PL osoby odbierającej."
                            : "Telefon zagraniczny: Pocztex zwykle OK; awaryjny InPost użyje numeru nadawcy PL jako kontakt kuriera."}
                        </p>
                      )}
                  </div>
                  {detail.shipping_is_foreign && (
                    <div className="rounded-lg border border-violet-700/40 bg-violet-100/70 px-3 py-2">
                      <p className="text-sm font-medium text-violet-950">
                        Nadanie na poczcie (Pocztex zagranica)
                      </p>
                      <p className="mt-1 text-xs text-violet-900/80">
                        Wypełniony blankiet A4: nadawca, odbiorca, etykieta na paczkę
                        {detail.shipping_city ? ` (${detail.shipping_city})` : ""}. Poza UE także
                        wzór deklaracji celnej.
                      </p>
                      <Button
                        variant="outline"
                        className="mt-2 w-full border-violet-700/50 bg-white text-violet-950 hover:bg-violet-50"
                        disabled={pocztexFormBusy}
                        onClick={() => void handlePocztexForeignForm(detail.id)}
                      >
                        {pocztexFormBusy ? (
                          <>
                            <ActionSpinner className="mr-2" />
                            Generowanie…
                          </>
                        ) : (
                          <>
                            <Printer className="mr-2 h-4 w-4" />
                            Wydruk blankietu Pocztex
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                  {detail.paczkomat_name && (
                    <div className="rounded-lg border border-brand/30 bg-brand/5 px-3 py-2">
                      <p className="font-medium text-foreground">
                        Paczkomat odbioru (Furgonetka)
                      </p>
                      <PaczkomatMatchStatusBadge order={detail} className="mt-1" />
                      {detail.paczkomat_delivery_address?.resolved ? (
                        <>
                          <p className="mt-1 font-mono text-xs">
                            {detail.paczkomat_delivery_address.point_code}
                          </p>
                          <p className="mt-1">
                            {formatAddressBlock(
                              detail.paczkomat_delivery_address.street,
                              detail.paczkomat_delivery_address.postcode,
                              detail.paczkomat_delivery_address.city,
                            )}
                          </p>
                          {detail.paczkomat_delivery_address.point_name &&
                            detail.paczkomat_delivery_address.point_name !==
                              detail.paczkomat_name && (
                              <p className="mt-1 text-xs text-muted-foreground">
                                {detail.paczkomat_delivery_address.point_name}
                              </p>
                            )}
                          {detail.paczkomat_delivery_address.corrected_from && (
                            <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
                              Skorygowano kod z zamówienia:{" "}
                              {detail.paczkomat_delivery_address.corrected_from} →{" "}
                              {detail.paczkomat_delivery_address.point_code}
                            </p>
                          )}
                        </>
                      ) : (
                        <>
                          <p className="mt-1">{detail.paczkomat_name}</p>
                          {detail.paczkomat_match_pending && !detail.furgonetka_package_id ? (
                            <p className="mt-1 text-xs text-muted-foreground">
                              Dopasowanie automatu w tle (co ok. 90 s). Możesz też wyszukać ręcznie.
                            </p>
                          ) : (
                            <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
                              Nie udało się dopasować adresu automatu w mapie Furgonetki. Sprawdź
                              kod paczkomatu przed wysyłką.
                            </p>
                          )}
                          {detail.paczkomat_delivery_address?.search_error && (
                            <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
                              {detail.paczkomat_delivery_address.search_error}
                            </p>
                          )}
                          <div className="mt-2 flex flex-wrap gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={detailPanelBusy}
                              onClick={() =>
                                detailId &&
                                paczkomatResolveMut.mutate({ id: detailId, apply: false })
                              }
                            >
                              {paczkomatResolveMut.isPending ? (
                                <>
                                  <ActionSpinner className="mr-2 h-3.5 w-3.5" />
                                  Szukam…
                                </>
                              ) : (
                                "Wyszukaj automat"
                              )}
                            </Button>
                            {(paczkomatCandidates.length > 0 ||
                              (detail.paczkomat_delivery_address?.candidates?.length ?? 0) > 0) && (
                              <Button
                                size="sm"
                                variant="default"
                                disabled={detailPanelBusy || !selectedPaczkomatCode || !detailId}
                                onClick={() => {
                                  if (!detailId || !selectedPaczkomatCode) return;
                                  const pool =
                                    paczkomatCandidates.length > 0
                                      ? paczkomatCandidates
                                      : detail.paczkomat_delivery_address?.candidates ?? [];
                                  const picked = pool.find((c) => c.code === selectedPaczkomatCode);
                                  paczkomatResolveMut.mutate({
                                    id: detailId,
                                    apply: true,
                                    point_code: selectedPaczkomatCode,
                                    service: picked?.service,
                                  });
                                }}
                              >
                                {paczkomatResolveMut.isPending ? (
                                  <>
                                    <ActionSpinner className="mr-2 h-3.5 w-3.5" />
                                    Zapisuję…
                                  </>
                                ) : (
                                  "Zastosuj kod"
                                )}
                              </Button>
                            )}
                          </div>
                          {(paczkomatCandidates.length > 0 ||
                            (detail.paczkomat_delivery_address?.candidates?.length ?? 0) > 0) && (
                            <div className="mt-2 space-y-1">
                              {(paczkomatCandidates.length > 0
                                ? paczkomatCandidates
                                : detail.paczkomat_delivery_address?.candidates ?? []
                              ).map((c) => (
                                <label
                                  key={`${c.service}-${c.code}`}
                                  className="flex cursor-pointer items-start gap-2 rounded border border-border/60 px-2 py-1 text-xs"
                                >
                                  <input
                                    type="radio"
                                    name="paczkomat-candidate"
                                    className="mt-0.5"
                                    checked={selectedPaczkomatCode === c.code}
                                    onChange={() => setSelectedPaczkomatCode(c.code)}
                                  />
                                  <span>
                                    <span className="font-mono font-medium">{c.code}</span>
                                    {" · "}
                                    {c.service.toUpperCase()}
                                    {c.label ? ` · ${c.label}` : ""}
                                    {(c.street || c.city) && (
                                      <>
                                        {" · "}
                                        {formatAddressBlock(c.street, c.postcode, c.city)}
                                      </>
                                    )}
                                  </span>
                                </label>
                              ))}
                            </div>
                          )}
                          {paczkomatResolveMsg && (
                            <p className="mt-2 text-xs text-muted-foreground">{paczkomatResolveMsg}</p>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
                <p className="mt-3 text-sm whitespace-pre-wrap">
                  <strong>Dedykacja:</strong>
                  <br />
                  {detail.dedication || "—"}
                </p>
                {detail.internal_notes && (
                  <p className="mt-3 text-sm whitespace-pre-wrap">
                    <strong>Notatki:</strong>
                    <br />
                    {detail.internal_notes}
                  </p>
                )}
              </>
            )}
            {!editingOrder && detail.paczkomat_name && (
              <p className="mt-2 text-sm">
                <strong>Rozpoznany kurier:</strong>{" "}
                {detail.paczkomat_courier_label ? (
                  <Badge variant="secondary">{detail.paczkomat_courier_label}</Badge>
                ) : (
                  <span className="text-muted-foreground">nie rozpoznano — wybierz ręcznie</span>
                )}
              </p>
            )}
            <p className="mt-4 text-xs text-muted-foreground">
              Flow: Nowe → zlecenie w Furgonetce → dedykacja → pakowanie → do wysyłki → nadane
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              {WORKFLOW_STEPS.map((st) => (
                <Button
                  key={st}
                  size="sm"
                  variant={detail.fulfillment_status === st ? "default" : "outline"}
                  onClick={() => patchMut.mutate({ id: detail.id, patch: { fulfillment_status: st } })}
                >
                  {STATUS_LABELS[st]}
                </Button>
              ))}
            </div>
            <p className="mt-3 text-xs text-muted-foreground">Wyjątki / zamknięcie</p>
            <div className="mt-1 flex flex-wrap gap-2">
              {EXCEPTION_STATUSES.map((st) => (
                <Button
                  key={st}
                  size="sm"
                  variant={detail.fulfillment_status === st ? "default" : "outline"}
                  className={cn(
                    st === "withdrawal" &&
                      detail.fulfillment_status !== st &&
                      "border-amber-500/50 text-amber-900 dark:text-amber-100",
                    st === "withdrawal" &&
                      detail.fulfillment_status === st &&
                      "bg-amber-600 hover:bg-amber-700 text-white border-amber-600",
                    st === "ready_to_reship" &&
                      detail.fulfillment_status !== st &&
                      "border-blue-600/50 text-blue-950",
                    st === "ready_to_reship" &&
                      detail.fulfillment_status === st &&
                      "bg-blue-700 hover:bg-blue-800 text-white border-blue-700",
                  )}
                  onClick={() =>
                    patchMut.mutate({ id: detail.id, patch: { fulfillment_status: st } })
                  }
                >
                  {STATUS_LABELS[st]}
                </Button>
              ))}
            </div>
            {detail.paczkomat_name && !detail.furgonetka_package_id && (
              <div className="mt-3 rounded-lg border border-brand/25 bg-brand/5 px-3 py-2 text-sm text-muted-foreground">
                <p>
                  <strong className="text-foreground">Paczkomat → paczkomat</strong> — odbiór:{" "}
                  <strong>{detail.paczkomat_name}</strong>
                  {detail.paczkomat_courier_label && <> ({detail.paczkomat_courier_label})</>}
                </p>
                <p className="mt-1 text-xs">
                  Doręczenie: automat z zamówienia. Nadanie:{" "}
                  <strong>
                    {fkConfig?.inpost_drop_point?.code === "any_apm"
                      ? "dowolny paczkomat InPost"
                      : fkConfig?.inpost_drop_point?.code || "dowolny paczkomat InPost"}
                  </strong>
                  {fkConfig?.inpost_drop_point?.label &&
                    fkConfig.inpost_drop_point.code !== "any_apm" &&
                    ` (${fkConfig.inpost_drop_point.label})`}{" "}
                  — bez odbioru kurierem. Po utworzeniu paczki w API ustaw w panelu Furgonetka:{" "}
                  <strong>Nadanie → Automat → Dowolny paczkomat → Wydrukuję dokumenty</strong>.
                </p>
              </div>
            )}
            {furgonetkaApiReady && !detail.furgonetka_package_id && (
              <div className="mt-4 flex flex-col gap-2">
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => void handleDetailQuote()}
                    disabled={detailPanelBusy}
                  >
                    {detailPreflightBusy || quoteMut.isPending ? (
                      <>
                        <ActionSpinner className="mr-2" />
                        Wycena…
                      </>
                    ) : (
                      "Wycena"
                    )}
                  </Button>
                  {canCreateFurgonetkaPackage(detail.fulfillment_status) && (
                    <Button
                      className="flex-1"
                      onClick={() => void handleDetailShip()}
                      disabled={detailPanelBusy}
                    >
                      {detailPreflightBusy || shipMut.isPending ? (
                        <>
                          <ActionSpinner className="mr-2" />
                          Tworzenie…
                        </>
                      ) : (
                        <>
                          <Send className="mr-2 h-4 w-4" />
                          Utwórz w Furgonetce
                        </>
                      )}
                    </Button>
                  )}
                </div>
                {!detail.paczkomat_name && canCreateFurgonetkaPackage(detail.fulfillment_status) && (
                  <Button
                    variant="secondary"
                    className="w-full"
                    onClick={() => shipInpostMut.mutate(detail.id)}
                    disabled={
                      shipMut.isPending || shipInpostMut.isPending || !inpostCourier?.service_id
                    }
                  >
                    Awaryjnie InPost Kurier → adres
                  </Button>
                )}
                {!detail.paczkomat_name && canCreateFurgonetkaPackage(detail.fulfillment_status) && (
                  <p className="text-xs text-muted-foreground">
                    Bez paczkomatu: główny przycisk = <strong>Pocztex</strong>. Awaryjnie InPost
                    działa też przy telefonie UK/DE (kontakt kuriera = numer nadawcy PL).
                  </p>
                )}
                {detailQuote?.price != null && (
                  <p className="text-sm text-muted-foreground">
                    Szacunkowa cena: <strong>{detailQuote.price.toFixed(2)} PLN</strong>
                  </p>
                )}
                {detailQuote?.success && (
                  <p className="text-sm text-emerald-600 dark:text-emerald-400">{detailQuote.success}</p>
                )}
                {detailQuote?.pickupManualFix && detailQuote.editUrl && (
                  <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm">
                    <p className="text-amber-900 dark:text-amber-100">
                      REST API nie zapisuje „Dowolny paczkomat” na paczce. Przed opłatą w edycji ustaw:{" "}
                      <strong>Nadanie → Automat → Dowolny paczkomat → Wydrukuję dokumenty</strong> (jak na
                      furgonetka.pl).
                    </p>
                    <a
                      href={detailQuote.editUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 inline-block text-sm font-medium text-brand underline"
                    >
                      Otwórz edycję paczki w Furgonetce →
                    </a>
                  </div>
                )}
                {(detailQuote?.error ||
                  (!quoteMut.isPending &&
                    !shipMut.isPending &&
                    !detailQuote?.success &&
                    detail.furgonetka_last_error)) &&
                  !detail.furgonetka_package_id && (
                  <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                    {detailQuote?.error || detail.furgonetka_last_error}
                  </div>
                )}
              </div>
            )}
            {!furgonetkaApiReady && furgonetkaBlockedReason && !detail.furgonetka_package_id && (
              <p className="mt-4 text-sm text-muted-foreground">{furgonetkaBlockedReason}</p>
            )}
            {detail.furgonetka_package_id && (
              <div className="mt-4 rounded-lg border border-brand/30 bg-brand/5 px-3 py-2 text-sm">
                Paczka Furgonetka: <code>{detail.furgonetka_package_id}</code>
                {detail.furgonetka_price_pln != null && (
                  <> — {detail.furgonetka_price_pln.toFixed(2)} PLN</>
                )}
                {detail.furgonetka_tracking && (
                  <p className="mt-1">
                    Tracking: <code>{detail.furgonetka_tracking}</code>
                  </p>
                )}
                {detail.furgonetka_status_synced_at ? (
                  <div className="mt-2 flex flex-wrap gap-2">
                    <Badge variant={detail.furgonetka_paid ? "success" : "warning"}>
                      {detail.furgonetka_paid ? "Opłacone" : "Do opłacenia"}
                    </Badge>
                    {detail.furgonetka_paid && isPackageDelivered(detail) ? (
                      <Badge variant="success">Doręczone</Badge>
                    ) : (
                      detail.furgonetka_paid &&
                      detail.furgonetka_dispatched && <Badge variant="outline">Nadane u kuriera</Badge>
                    )}
                    {detail.furgonetka_status_label && (
                      <span className="text-xs text-muted-foreground">{detail.furgonetka_status_label}</span>
                    )}
                  </div>
                ) : (
                  <p className="mt-1 text-xs text-muted-foreground">
                    Paczka zlecona. Status: do opłacenia (odśwież „Status Furgonetka” po płatności).
                  </p>
                )}
                {!detail.furgonetka_paid && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    Opłać paczkę w panelu Furgonetki, potem pobierz lub wydrukuj etykietę poniżej.
                  </p>
                )}
              </div>
            )}
            {detail.furgonetka_package_id && furgonetkaApiReady && (
              <div className="mt-4 flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  disabled={labelBusy}
                  onClick={() => handleLabel(detail.id, "download")}
                >
                  <FileDown className="mr-2 h-4 w-4" />
                  Pobierz etykietę
                </Button>
                <Button
                  className="flex-1"
                  disabled={labelBusy}
                  onClick={() => handleLabel(detail.id, "print")}
                >
                  <Printer className="mr-2 h-4 w-4" />
                  Drukuj
                </Button>
              </div>
            )}
            {detail.furgonetka_tracking && (
              <p className="mt-3 text-sm">
                Tracking: <code>{detail.furgonetka_tracking}</code>
              </p>
            )}
              </>
            ) : null}
          </motion.div>
        </div>
      )}
    </div>
  );
}

function OrderEditForm({
  form,
  onChange,
  onCancel,
  onSave,
  saving,
}: {
  form: FulfillmentOrderPatch;
  onChange: (next: FulfillmentOrderPatch) => void;
  onCancel: () => void;
  onSave: () => void;
  saving: boolean;
}) {
  const set = (key: keyof FulfillmentOrderPatch, value: string) =>
    onChange({ ...form, [key]: value });

  const field = (
    id: string,
    label: string,
    key: keyof FulfillmentOrderPatch,
    opts?: { multiline?: boolean; placeholder?: string },
  ) => (
    <label className="block text-sm" htmlFor={id}>
      <span className="font-medium">{label}</span>
      {opts?.multiline ? (
        <textarea
          id={id}
          className="mt-1 flex min-h-[72px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          value={(form[key] as string) ?? ""}
          placeholder={opts.placeholder}
          onChange={(e) => set(key, e.target.value)}
        />
      ) : (
        <Input
          id={id}
          className="mt-1"
          value={(form[key] as string) ?? ""}
          placeholder={opts?.placeholder}
          onChange={(e) => set(key, e.target.value)}
        />
      )}
    </label>
  );

  return (
    <div className="mt-3 space-y-3 border-t pt-3">
      {field("edit-customer-name", "Imię i nazwisko", "customer_name")}
      {field("edit-email", "E-mail", "customer_email", { placeholder: "email@example.com" })}
      {field("edit-phone", "Telefon", "phone", { placeholder: "+48… lub +49…" })}
      {field("edit-shipping-name", "Nazwa na przesyłce (opcjonalnie)", "shipping_name")}
      {field("edit-street", "Ulica i numer", "shipping_street")}
      <div className="grid grid-cols-2 gap-2">
        {field("edit-postcode", "Kod pocztowy", "shipping_postal_code", { placeholder: "00-000" })}
        {field("edit-city", "Miasto", "shipping_city")}
      </div>
      {field("edit-dedication", "Dedykacja", "dedication", {
        multiline: true,
        placeholder: "Tekst dedykacji w książce",
      })}
      {field("edit-paczkomat", "Nazwa / kod paczkomatu", "paczkomat_name", {
        placeholder: "np. ZGO40M lub ZG040M",
      })}
      {field("edit-notes", "Notatki wewnętrzne", "internal_notes", { multiline: true })}
      <div className="flex gap-2 pt-1">
        <Button className="flex-1" onClick={onSave} disabled={saving}>
          <Check className="mr-2 h-4 w-4" />
          {saving ? "Zapisuję…" : "Zapisz"}
        </Button>
        <Button variant="outline" className="flex-1" onClick={onCancel} disabled={saving}>
          Anuluj
        </Button>
      </div>
    </div>
  );
}

function Kpi({
  title,
  value,
  icon: Icon,
}: {
  title: string;
  value: number;
  icon: React.ComponentType<{ className?: string }>;
}) {
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
      <Card>
        <CardContent className="flex items-center gap-4 p-5">
          <div className="rounded-lg bg-brand/10 p-3">
            <Icon className="h-5 w-5 text-brand" />
          </div>
          <div>
            <div className="text-2xl font-bold tabular-nums">{value}</div>
            <div className="text-xs text-muted-foreground">{title}</div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
